import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { 
  createDependency, 
  updateDependency,
  clearError 
} from '../../../store/slices/dependenciesSlice';
import { fetchEpics } from '../../../store/slices/epicsSlice';
import { fetchFeatures } from '../../../store/slices/featuresSlice';
import { fetchPrograms } from '../../../store/slices/programsSlice';
import { fetchInvestibleStructures } from '../../../store/slices/investibleStructuresSlice';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { 
  Dependency,
  DependencyType, 
  DependencySeverity, 
  EntityType 
} from '../../../types';

interface DependencyFormProps {
  dependency?: Dependency;
  onSuccess: () => void;
  onCancel: () => void;
}

interface EntityOption {
  value: string;
  label: string;
  type: EntityType;
}

export const DependencyForm: React.FC<DependencyFormProps> = ({
  dependency,
  onSuccess,
  onCancel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.dependencies);
  const { epics } = useSelector((state: RootState) => state.epics);
  const { features } = useSelector((state: RootState) => state.features);
  const { programs } = useSelector((state: RootState) => state.programs);
  const { structures: investibleStructures } = useSelector((state: RootState) => state.investibleStructures);

  const [formData, setFormData] = useState({
    name: dependency?.name || '',
    description: dependency?.description || '',
    type: dependency?.type || DependencyType.BLOCKING,
    sourceType: dependency?.sourceType || EntityType.EPIC,
    sourceId: dependency?.sourceId || '',
    targetType: dependency?.targetType || EntityType.EPIC,
    targetId: dependency?.targetId || '',
    severity: dependency?.severity || DependencySeverity.MEDIUM,
    expectedDate: dependency?.expectedDate ? dependency.expectedDate.split('T')[0] : '',
  });

  const [entityOptions, setEntityOptions] = useState<EntityOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<EntityOption[]>([]);
  const [targetOptions, setTargetOptions] = useState<EntityOption[]>([]);

  useEffect(() => {
    // Load entity data
    dispatch(fetchEpics());
    dispatch(fetchFeatures());
    dispatch(fetchPrograms());
    dispatch(fetchInvestibleStructures());
  }, [dispatch]);

  useEffect(() => {
    // Build entity options
    const options: EntityOption[] = [];
    
    epics.forEach(epic => {
      options.push({
        value: epic.id,
        label: `${epic.name} (${epic.investibleStructure?.name || 'Unknown IS'})`,
        type: EntityType.EPIC,
      });
    });
    
    features.forEach(feature => {
      options.push({
        value: feature.id,
        label: `${feature.name} (${feature.epic?.name || 'Unknown Epic'})`,
        type: EntityType.FEATURE,
      });
    });
    
    programs.forEach(program => {
      options.push({
        value: program.id,
        label: program.name,
        type: EntityType.PROGRAM,
      });
    });
    
    investibleStructures.forEach((is: any) => {
      options.push({
        value: is.id,
        label: is.name,
        type: EntityType.INVESTIBLE_STRUCTURE,
      });
    });

    setEntityOptions(options);
  }, [epics, features, programs, investibleStructures]);

  useEffect(() => {
    // Filter options based on selected entity types
    setSourceOptions(entityOptions.filter(option => option.type === formData.sourceType));
    setTargetOptions(entityOptions.filter(option => option.type === formData.targetType));
  }, [entityOptions, formData.sourceType, formData.targetType]);

  useEffect(() => {
    // Clear source/target IDs when type changes
    if (formData.sourceType !== dependency?.sourceType) {
      setFormData(prev => ({ ...prev, sourceId: '' }));
    }
    if (formData.targetType !== dependency?.targetType) {
      setFormData(prev => ({ ...prev, targetId: '' }));
    }
  }, [formData.sourceType, formData.targetType, dependency]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    if (!formData.sourceId || !formData.targetId) {
      return;
    }

    if (formData.sourceType === formData.targetType && formData.sourceId === formData.targetId) {
      return;
    }

    try {
      const dependencyData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type,
        sourceType: formData.sourceType,
        sourceId: formData.sourceId,
        targetType: formData.targetType,
        targetId: formData.targetId,
        severity: formData.severity,
        expectedDate: formData.expectedDate ? new Date(formData.expectedDate).toISOString() : undefined,
      };

      if (dependency) {
        await dispatch(updateDependency({
          id: dependency.id,
          ...dependencyData,
        })).unwrap();
      } else {
        await dispatch(createDependency(dependencyData)).unwrap();
      }

      onSuccess();
    } catch (error) {
      console.error('Failed to save dependency:', error);
    }
  };

  const entityTypeOptions = [
    { value: EntityType.EPIC, label: 'Epic' },
    { value: EntityType.FEATURE, label: 'Feature' },
    { value: EntityType.PROGRAM, label: 'Program' },
    { value: EntityType.INVESTIBLE_STRUCTURE, label: 'Investible Structure' },
  ];

  const dependencyTypeOptions = [
    { value: DependencyType.BLOCKING, label: 'Blocking' },
    { value: DependencyType.ENABLING, label: 'Enabling' },
    { value: DependencyType.INFORMATIONAL, label: 'Informational' },
  ];

  const severityOptions = [
    { value: DependencySeverity.CRITICAL, label: 'Critical' },
    { value: DependencySeverity.HIGH, label: 'High' },
    { value: DependencySeverity.MEDIUM, label: 'Medium' },
    { value: DependencySeverity.LOW, label: 'Low' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Basic Information */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">Basic Information</h3>

        <Input
          label="Dependency Name"
          value={formData.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
          placeholder="Enter dependency name"
          required
        />

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-foreground">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('description', e.target.value)}
            placeholder="Describe the dependency relationship"
            className="w-full min-h-[72px] px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Dependency Type"
            value={formData.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            options={dependencyTypeOptions}
            required
          />

          <Select
            label="Severity"
            value={formData.severity}
            onChange={(e) => handleInputChange('severity', e.target.value)}
            options={severityOptions}
            required
          />
        </div>

        <Input
          label="Expected Resolution Date"
          type="date"
          value={formData.expectedDate}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('expectedDate', e.target.value)}
        />
      </div>

      {/* Source Entity */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">Source Entity (Depends On)</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Source Type"
            value={formData.sourceType}
            onChange={(e) => handleInputChange('sourceType', e.target.value)}
            options={entityTypeOptions}
            required
          />

          <Select
            label="Source Entity"
            value={formData.sourceId}
            onChange={(e) => handleInputChange('sourceId', e.target.value)}
            options={sourceOptions.map(option => ({
              value: option.value,
              label: option.label,
            }))}
            placeholder="Select source entity"
            required
            disabled={!formData.sourceType}
          />
        </div>
      </div>

      {/* Target Entity */}
      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">Target Entity (Is Blocked)</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select
            label="Target Type"
            value={formData.targetType}
            onChange={(e) => handleInputChange('targetType', e.target.value)}
            options={entityTypeOptions}
            required
          />

          <Select
            label="Target Entity"
            value={formData.targetId}
            onChange={(e) => handleInputChange('targetId', e.target.value)}
            options={targetOptions.map(option => ({
              value: option.value,
              label: option.label,
            }))}
            placeholder="Select target entity"
            required
            disabled={!formData.targetType}
          />
        </div>
      </div>

      {/* Validation Messages */}
      {formData.sourceType === formData.targetType && formData.sourceId === formData.targetId && formData.sourceId && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-md p-3">
          <p className="text-status-error text-xs font-medium">
            An entity cannot depend on itself. Please select different source and target entities.
          </p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-status-error/10 border border-status-error/30 rounded-md p-3">
          <p className="text-status-error text-xs font-medium">{error}</p>
        </div>
      )}

      {/* Form Actions */}
      <div className="flex justify-end gap-2 pt-3 border-t border-border">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={
            isLoading ||
            !formData.name.trim() ||
            !formData.sourceId ||
            !formData.targetId ||
            (formData.sourceType === formData.targetType && formData.sourceId === formData.targetId)
          }
          className="flex items-center space-x-2"
        >
          {isLoading && <LoadingSpinner size="sm" />}
          <span>{dependency ? 'Update' : 'Create'} Dependency</span>
        </Button>
      </div>
    </form>
  );
};